import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function PolitykaPrywatnosci() {
  const { t } = useTranslation();
  return (
    <>
      <Helmet>
        <title>Polityka prywatności | ApisAI</title>
        <meta name="description" content="Polityka prywatności serwisu ApisAI - jak przetwarzamy Twoje dane osobowe." />
      </Helmet>

      <div className="min-h-screen bg-[#0B0F1A]">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-white/5">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Link to="/" className="text-gray-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <span className="font-semibold text-white">Polityka prywatności</span>
          </div>
        </nav>

        {/* Content */}
        <main className="pt-24 pb-16 px-6">
          <div className="max-w-3xl mx-auto prose prose-invert prose-gray">
            <h1 className="text-3xl font-bold text-white mb-8">Polityka prywatności</h1>
            
            <p className="text-gray-400 mb-6">Ostatnia aktualizacja: 24 stycznia 2026</p>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">1. Administrator danych</h2>
              <p className="text-gray-300">
                Administratorem danych osobowych jest ApisAI. Kontakt: kontakt@apisai.pl
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">2. Jakie dane zbieramy</h2>
              <p className="text-gray-300 mb-4">Zbieramy następujące dane:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-300">
                <li><strong>Dane konta:</strong> email, hasło (zaszyfrowane), imię</li>
                <li><strong>Dane hodowli:</strong> nazwa, adres, dane kontaktowe, zdjęcia</li>
                <li><strong>Dane płatności:</strong> przetwarzane przez Stripe (nie przechowujemy danych kart)</li>
                <li><strong>Dane techniczne:</strong> adres IP, typ przeglądarki, logi serwera</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">3. Cel przetwarzania</h2>
              <p className="text-gray-300 mb-4">Dane przetwarzamy w celu:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-300">
                <li>Świadczenia usług serwisu (podstawa: umowa)</li>
                <li>Obsługi płatności (podstawa: umowa)</li>
                <li>Wysyłki powiadomień o leadach (podstawa: umowa)</li>
                <li>Analizy i ulepszania serwisu (podstawa: uzasadniony interes)</li>
                <li>Marketingu własnych usług (podstawa: zgoda)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">4. Odbiorcy danych</h2>
              <p className="text-gray-300 mb-4">Dane mogą być przekazywane:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-300">
                <li>Stripe — obsługa płatności</li>
                <li>MongoDB Atlas — hosting bazy danych</li>
                <li>Emergent — hosting aplikacji</li>
                <li>Google Analytics — analityka (jeśli wyrażono zgodę)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">5. Okres przechowywania</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-300">
                <li>Dane konta: do usunięcia konta + 30 dni</li>
                <li>Dane płatności: zgodnie z wymogami prawnymi (5 lat)</li>
                <li>Logi serwera: 90 dni</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">6. Twoje prawa</h2>
              <p className="text-gray-300 mb-4">Masz prawo do:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-300">
                <li>Dostępu do swoich danych</li>
                <li>Sprostowania danych</li>
                <li>Usunięcia danych ("prawo do bycia zapomnianym")</li>
                <li>Ograniczenia przetwarzania</li>
                <li>Przenoszenia danych</li>
                <li>Sprzeciwu wobec przetwarzania</li>
                <li>Wycofania zgody (bez wpływu na wcześniejsze przetwarzanie)</li>
                <li>Skargi do organu nadzorczego (UODO)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">7. Bezpieczeństwo</h2>
              <p className="text-gray-300">
                Stosujemy odpowiednie środki techniczne i organizacyjne, w tym szyfrowanie SSL/TLS, 
                hashowanie haseł, regularne kopie zapasowe i kontrolę dostępu.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">8. Zmiany polityki</h2>
              <p className="text-gray-300">
                O istotnych zmianach polityki prywatności poinformujemy drogą elektroniczną 
                z 14-dniowym wyprzedzeniem.
              </p>
            </section>

            <div className="mt-12 pt-8 border-t border-white/10">
              <p className="text-gray-500 text-sm">
                Kontakt w sprawach ochrony danych: kontakt@apisai.pl
              </p>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t border-white/10 py-8">
          <div className="max-w-4xl mx-auto px-6 flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500">
            <Link to="/regulamin" className="hover:text-white transition-colors">Regulamin</Link>
            <Link to="/polityka-prywatnosci" className="hover:text-white transition-colors">Polityka prywatności</Link>
            <Link to="/polityka-cookies" className="hover:text-white transition-colors">Cookies</Link>
            <Link to="/kontakt" className="hover:text-white transition-colors">Kontakt</Link>
          </div>
        </footer>
      </div>
    </>
  );
}
